import React, { useEffect, useState, useRef } from "react";
import { saveChecklist, loadChecklist } from "./checklistStorage";
import Env from "../../runtimeEnv";
import confetti from "canvas-confetti";
import { FiUpload } from "react-icons/fi";

const TherapistHome = ({
  therapistName,
  therapistId,
  quickActions,
  checklistData,
  onInternalLink,
}) => {
  const [expanded, setExpanded] = useState(true);
  const [checkedItems, setCheckedItems] = useState({});
  const [uploadingItemId, setUploadingItemId] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [uploadProgress, setUploadProgress] = useState({});
  const uploadIntervals = useRef({});

  useEffect(() => {
    if (!therapistId) return;

    const fetchChecklistState = async () => {
      const saved = await loadChecklist(therapistId);
      setCheckedItems(saved);
    };

    fetchChecklistState();
  }, [therapistId]);

    const completedCount = checklistData.filter(
    (item) => checkedItems[item.id],
  ).length;

  const totalCount = checklistData.length;

  useEffect(() => {
    if (totalCount > 0 && completedCount === totalCount) {
      confetti({
        particleCount: 150,
        spread: 90,
        origin: { y: 0.6 },
      });
    }
  }, [completedCount, totalCount]);

  const toggleCheck = (id) => {
    if (!therapistId) return;

    setCheckedItems((prev) => {
      const updatedValue = !prev[id];

      // always clear any upload UI/progress
      clearInterval(uploadIntervals.current[id]);

      setUploadProgress((prevProgress) => ({
        ...prevProgress,
        [id]: 0,
      }));

      if (uploadingItemId === id) {
        setUploadingItemId(null);
      }

      const updated = {
        ...prev,
        [id]: updatedValue,
      };

      saveChecklist(therapistId, updated);
      return updated;
    });
  };

  const handleFileSelect = (id, files) => {
    setSelectedFiles((prev) => ({
      ...prev,
      [id]: Array.from(files || []),
    }));
  };

  // progress bar
  const startFakeUploadProgress = (itemId) => {
    clearInterval(uploadIntervals.current[itemId]);

    let progress = 0;

    setUploadProgress((prev) => ({
      ...prev,
      [itemId]: 0,
    }));

    uploadIntervals.current[itemId] = setInterval(() => {
      progress += 8;

      if (progress >= 75) {
        progress = 75;
        clearInterval(uploadIntervals.current[itemId]);
      }

      setUploadProgress((prev) => ({
        ...prev,
        [itemId]: progress,
      }));
    }, 200);
  };

  const handleUpload = async (item) => {
    const files = selectedFiles[item.id] || [];

    const validFiles = files.filter(
      (file) => file && file.size > 0 && file.name?.trim(),
    );

    if (validFiles.length === 0) {
      alert("Please select at least one valid file before uploading.");
      return;
    }

    if (validFiles.length > 5 || validFiles.reduce((sum, file) => sum + file.size, 0) > 4 * 1024 * 1024 ||
        validFiles.some((file) => !/\.(pdf|png|jpe?g)$/i.test(file.name))) {
      alert('Choose up to 5 PDF, PNG, or JPEG files totaling 4 MiB or less.');
      return;
    }

    try {
      startFakeUploadProgress(item.id);

      const formData = new FormData();

      formData.append("therapistId", therapistId);
      formData.append("itemId", item.id);
      formData.append("itemName", item.name);

      validFiles.forEach((file) => {
        formData.append("files", file);
      });

      const response = await fetch(
        `${Env.REACT_APP_PYTHON_API_URL}/api/therapist-admin/upload-document`,
        { credentials: "include",
          method: "POST",
          body: formData,
        },
      );

      if (!response.ok) {
        if (response.status === 413) {
          throw new Error(
            "Files must total 4 MiB or less per upload."
          );
        }

        throw new Error("Upload failed");
      }

      clearInterval(uploadIntervals.current[item.id]);

      setUploadProgress((prev) => ({
        ...prev,
        [item.id]: 100,
      }));

      setTimeout(() => {
        toggleCheck(item.id);
        setUploadingItemId(null);

        setUploadProgress((prev) => ({
          ...prev,
          [item.id]: 0,
        }));

        setSelectedFiles((prev) => ({
          ...prev,
          [item.id]: [],
        }));

        alert("Upload successful.");
      }, 400);
    } catch (err) {
      console.error("TherapistHome.js: error event");

      clearInterval(uploadIntervals.current[item.id]);

      setUploadProgress((prev) => ({
        ...prev,
        [item.id]: 0,
      }));

      setUploadingItemId(null);

      alert(err.message || "Upload failed.");
    }
  };



  return (
    <div className="min-h-screen px-12 py-12">
      {/* Welcome */}
      <div className="mb-10">
        <h1 className="text-4xl font-bold mb-2">Welcome, {therapistName}</h1>

        <p className="text-gray-600 text-lg">
          Here’s your dashboard for today.
        </p>
      </div>

      {/* Quick Actions */}
      <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-4 mb-10">
        {quickActions.map((action) => (
          <button
            key={action.id}
            onClick={action.onClick}
            className="bg-white rounded-xl border border-gray-200 shadow-md p-6 text-left cursor-pointer transition-all duration-200 hover:shadow-xl hover:-translate-y-1 hover:border-blue-300 hover:bg-blue-50/30"
          >
            <div className="flex items-center gap-4">
              {action.icon && (
                <img
                  src={action.icon}
                  alt=""
                  className="w-8 h-8 flex-shrink-0"
                />
              )}

              <h3 className="font-semibold text-lg">{action.name}</h3>
            </div>
          </button>
        ))}
      </div>

      {/* Checklist */}
      <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
        <button
          className="w-full flex justify-between items-center"
          onClick={() => setExpanded(!expanded)}
        >
          <div>
            <h2 className="text-2xl font-bold text-left">
              Getting Started Checklist
            </h2>

            <p className="text-sm text-gray-500 mt-1 text-left">
              {completedCount} / {totalCount} completed
            </p>
          </div>

          <span className="text-2xl font-light">{expanded ? "−" : "+"}</span>
        </button>

        {expanded && (
          <div className="mt-6 space-y-5">
            {checklistData.map((item) => (
              <div key={item.id} className="flex flex-col gap-3">
                <div className="flex items-start gap-4">
                  {item.requiresUpload ? (
                    checkedItems[item.id] ? (
                      <input
                        type="checkbox"
                        checked={true}
                        onChange={() => toggleCheck(item.id)}
                        className="mt-1 w-5 h-5 cursor-pointer"
                      />
                    ) : (
                      <button
  type="button"
  onClick={() => setUploadingItemId(item.id)}
  className="mt-1 w-5 h-5 flex items-center justify-center text-blue-600 hover:text-blue-800 transition-colors"
  title="Upload file"
>
  <FiUpload size={18} />
</button>
                    )
                  ) : (
                    <input
                      type="checkbox"
                      checked={checkedItems[item.id] || false}
                      onChange={() => toggleCheck(item.id)}
                      className="mt-1 w-5 h-5 cursor-pointer"
                    />
                  )}

                  <div>
                    {item.internalLink ? (
                      <button
                        type="button"
                        onClick={() => onInternalLink?.(item.internalLink)}
                        className={`font-medium underline text-left ${
                          checkedItems[item.id]
                            ? "line-through text-gray-400"
                            : "text-blue-600 hover:text-blue-800"
                        }`}
                      >
                        {item.name}
                      </button>
                    ) : item.link ? (
                      <a
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`font-medium underline ${
                          checkedItems[item.id]
                            ? "line-through text-gray-400"
                            : "text-blue-600 hover:text-blue-800"
                        }`}
                      >
                        {item.name}
                      </a>
                    ) : (
                      <p
                        className={`font-medium ${
                          checkedItems[item.id]
                            ? "line-through text-gray-400"
                            : "text-black"
                        }`}
                      >
                        {item.name}
                      </p>
                    )}

                    {(item.description ||
                      item.descriptionLinks?.length > 0) && (
                      <div className="text-sm text-gray-500">
                        {item.description}

                        {item.descriptionLinks?.length > 0 && (
                          <>
                            {item.description ? " " : ""}
                            {item.descriptionLinks.map((link, index) => (
                              <React.Fragment key={index}>
                                <a
                                  href={link.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline hover:text-blue-800"
                                >
                                  {link.label}
                                </a>

                                {index < item.descriptionLinks.length - 1 &&
                                  ", "}
                              </React.Fragment>
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {uploadingItemId === item.id && item.requiresUpload && (
                  <div className="ml-9 flex flex-col gap-3">
                    <input
                      type="file"
                      multiple
                      onChange={(e) =>
                        handleFileSelect(item.id, e.target.files)
                      }
                    />

                    {selectedFiles[item.id]?.length > 0 && (
                      <p className="text-sm text-gray-500">
                        {selectedFiles[item.id].length} file
                        {selectedFiles[item.id].length === 1 ? "" : "s"}{" "}
                        selected
                      </p>
                    )}

                    <button
                      onClick={() => handleUpload(item)}
                      className="w-fit px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Upload
                    </button>

                    {uploadProgress[item.id] > 0 && (
                      <div className="w-64 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-600 transition-all duration-200"
                          style={{
                            width: `${uploadProgress[item.id]}%`,
                          }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TherapistHome;
